import { USD_DECIMALS, percentOfAmount } from '@funkit/connect-core'
import { Pressable, StyleSheet, useWindowDimensions } from 'react-native'
import Animated, { Keyframe, LinearTransition } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { AmountDisplay } from '../components/AmountDisplay'
import { useFullHeightStep } from '../flow/flowStepHeight'
import { NumPad } from '../components/NumPad'
import { PillButton } from '../components/PillButton'
import { TopBar } from '../components/TopBar'
import { WithdrawSourceCard } from '../components/WithdrawSourceCard'
import { FigmaSwapIcon } from '../icons/FigmaSystemIcons'
import { TokenChainIcon } from '../icons/TokenChainIcon'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import { chainIconUri } from '../utils/chainDisplay'
import { formatTokenAmount, groupThousands } from '../utils/formatAmount'
import { MOTION_DURATION, MOTION_EASING, nativeMotion } from '../utils/motion'
import { applyNumpadKey } from '../utils/numpadInput'

const NUMPAD_BOTTOM_MARGIN = 25
const NUMPAD_MIN_BOTTOM_MARGIN = 40

const AnimatedBox = Animated.createAnimatedComponent(Box)

// The card is what the button displaces, so it rides up with it instead of
// jumping to the place the button leaves it.
const CARD_TRANSITION =
  LinearTransition.duration(MOTION_DURATION).easing(MOTION_EASING)
// Continue rises out of the card's foot. Short, and fading as it goes: a full
// travel would have it cross the keypad on the way in.
const BUTTON_TRAVEL = 24
const BUTTON_ENTERING = new Keyframe({
  0: { opacity: 0, transform: [{ translateY: BUTTON_TRAVEL }] },
  100: { opacity: 1, transform: [{ translateY: 0 }], easing: MOTION_EASING },
}).duration(MOTION_DURATION)
const BUTTON_EXITING = new Keyframe({
  0: { opacity: 1, transform: [{ translateY: 0 }] },
  100: {
    opacity: 0,
    transform: [{ translateY: BUTTON_TRAVEL }],
    easing: MOTION_EASING,
  },
}).duration(MOTION_DURATION)

export interface WithdrawAmountSource {
  /** The source as the host names it, e.g. `USDC (Perps)`. */
  label: string
  /** What the amount and the balance are denominated in, e.g. `USDG`. */
  symbol: string
  chainId: number
  /** Direct token logo URL; falls back to a CDN lookup by symbol. */
  iconSrc?: string
}

export interface WithdrawAmountScreenProps {
  title: string
  /** The withdrawal amount, in source-token units. */
  amount: string
  /** Sets the amount outright, in source-token units (Use Max). */
  onChangeAmount: (amount: string) => void
  /**
   * The number in the amount display, which the numpad edits — dollars when
   * {@link inUsd}, else source-token units. Held by the caller so it survives
   * this screen leaving the stack.
   */
  input: string
  onChangeInput: (input: string) => void
  /** Whether the amount is being typed in dollars rather than token units. */
  inUsd: boolean
  /** The amount in dollars. `undefined` when nothing prices it. */
  usdLabel: string | undefined
  /** Switches denomination. `undefined` drops the swap entirely. */
  onSwapDenomination: (() => void) | undefined
  /** Fraction digits the source token carries; caps what the numpad accepts. */
  sourceDecimals?: number
  /** Spendable balance in source-token units. Omit to drop Use Max. */
  balance?: string
  /** What the withdrawal is drawn from. */
  source: WithdrawAmountSource
  /** Opens the source picker. Omit when the host offers a single source. */
  onPressSource?: () => void
  /** Replaces the converted amount and tints the amount. */
  errorText?: string
  canContinue: boolean
  onContinue: () => void
  onBack: () => void
  onClose: () => void
}

/**
 * Step one of the crypto withdrawal: how much, and what it comes out of. The
 * destination, the recipient, the fees and the signature are the step that
 * follows.
 *
 * The amount is driven by an in-app numpad rather than the system keyboard, so
 * the sheet never has to move out from under it.
 */
export function WithdrawAmountScreen({
  title,
  amount,
  onChangeAmount,
  input,
  onChangeInput,
  inUsd,
  usdLabel,
  onSwapDenomination,
  sourceDecimals,
  balance,
  source,
  onPressSource,
  errorText,
  canContinue,
  onContinue,
  onBack,
  onClose,
}: WithdrawAmountScreenProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const { height: windowHeight } = useWindowDimensions()
  // A full-height step owns its container. On native that container is the
  // screen, so the window height is the same number. On web the flow can sit in
  // a host box smaller than the window, and a window-height step then overflows
  // it — the foot of the numpad ends up below the host's clip.
  const height = useFullHeightStep(windowHeight)
  const insets = useSafeAreaInsets()

  // Use Max has nothing to take without a balance, and it gives way to Continue
  // as soon as there is an amount to submit.
  const showUseMax = input === '' && balance !== undefined

  // The same amount in the denomination not being typed in — falling back to
  // the token when nothing prices it, so the units are still named somewhere.
  // Display only — `amount` keeps every digit, so Use Max still spends the
  // whole balance rather than a truncated copy of it.
  const tokenLabel = `${formatTokenAmount(amount === '' ? '0' : amount)} ${source.symbol}`
  const convertedLabel = inUsd ? tokenLabel : (usdLabel ?? tokenLabel)
  // The error takes the line over, so the swap goes with the figure it labelled.
  const canSwap = !!onSwapDenomination && !errorText
  const convertedLine = (
    <Box flexDirection="row" alignItems="center" style={styles.convertedRow}>
      <Text
        variant="bodyMedium"
        numberOfLines={1}
        // Without this the text can't shrink, so a long label overflows the row
        // and pushes the swap icon off screen instead of ellipsising.
        style={{
          flexShrink: 1,
          color: errorText
            ? theme.content.elements.error
            : theme.content.elements.secondary,
        }}
      >
        {errorText ?? convertedLabel}
      </Text>
      {canSwap ? (
        <FigmaSwapIcon size={16} color={theme.content.elements.secondary} />
      ) : null}
    </Box>
  )

  return (
    <Box
      style={{
        height,
        paddingTop: insets.top,
        backgroundColor: theme.background.modal,
      }}
    >
      <TopBar title={title} onBack={onBack} onClose={onClose} />
      <Box style={styles.amount}>
        <AmountDisplay
          value={groupThousands(input)}
          prefix={inUsd ? '$' : ''}
          hasError={!!errorText}
          accessibilityLabel={t('withdrawAmount.amountLabel')}
        />
        {/* The same money in the other denomination, and the control that
            switches to it. Inert without a rate to convert at. */}
        <Box style={styles.converted}>
          {canSwap ? (
            <Pressable
              onPress={onSwapDenomination}
              accessibilityRole="button"
              accessibilityLabel={t('withdrawAmount.swapDenomination')}
              hitSlop={8}
            >
              {convertedLine}
            </Pressable>
          ) : (
            convertedLine
          )}
        </Box>
      </Box>

      {/* All the slack a taller device brings: the amount stays under the top
          bar and everything below stays pinned above the keypad. */}
      <Box flex={1} />

      <AnimatedBox layout={nativeMotion(CARD_TRANSITION)} style={styles.card}>
        <WithdrawSourceCard
          icon={
            <TokenChainIcon
              symbol={source.symbol}
              iconSrc={source.iconSrc}
              chainIconSrc={chainIconUri(source.chainId)}
              size={36}
            />
          }
          title={source.label}
          subtitle={
            balance ? `${groupThousands(balance)} ${source.symbol}` : undefined
          }
          useMaxLabel={t('withdrawAmount.useMax')}
          onUseMax={
            showUseMax && balance
              ? () =>
                  onChangeAmount(percentOfAmount(balance, 100, sourceDecimals))
              : undefined
          }
          onPress={onPressSource}
          accessibilityLabel={t('withdrawCrypto.sendFrom')}
          attachedToButton={!showUseMax}
        />
        {/* The window the button travels through, and never the button: a view
            can't crop what it is carrying. It outlives the button so the way
            out is cropped as well as the way in, and closes to nothing on the
            same clock — which is also what walks the card back down. */}
        <AnimatedBox
          layout={nativeMotion(CARD_TRANSITION)}
          style={[styles.buttonClip, showUseMax ? null : styles.buttonSlot]}
        >
          {showUseMax ? null : (
            <AnimatedBox
              entering={nativeMotion(BUTTON_ENTERING)}
              exiting={nativeMotion(BUTTON_EXITING)}
            >
              <PillButton
                label={t('common.continue')}
                radius="element"
                disabled={!canContinue}
                onPress={onContinue}
              />
            </AnimatedBox>
          )}
        </AnimatedBox>
      </AnimatedBox>

      <Box
        style={[
          styles.numpad,
          {
            marginBottom: Math.max(
              NUMPAD_BOTTOM_MARGIN + insets.bottom,
              NUMPAD_MIN_BOTTOM_MARGIN,
            ),
          },
        ]}
      >
        <NumPad
          backspaceAccessibilityLabel={t('withdrawAmount.delete')}
          // Dollars are typed to cents whatever the token's precision is.
          onPressKey={(key) =>
            onChangeInput(
              applyNumpadKey(input, key, inUsd ? USD_DECIMALS : sourceDecimals),
            )
          }
        />
      </Box>
    </Box>
  )
}

const styles = StyleSheet.create({
  amount: { alignItems: 'center', paddingTop: 78 },
  converted: { paddingTop: 32 },
  convertedRow: { gap: 6 },
  card: { marginHorizontal: 24 },
  buttonClip: { overflow: 'hidden' },
  buttonSlot: { marginTop: -19 },
  numpad: {
    marginHorizontal: 64,
    marginTop: 19,
    height: 240,
    minHeight: 176,
    flexShrink: 1,
  },
})
